import { useEffect, useMemo, useRef, useState } from 'react'
import { FaTrashAlt, FaUserPlus } from 'react-icons/fa'
import { BeatLoader } from 'react-spinners'
import Button from '../../../components/atoms/Buttons'
import CloseIcon from '../../../assets/icons/actions/close.svg'
import { createToast } from '../../../utils/Toast'
import { getFolderGrants, getFileAdmins, updateFolderGrants, formatFileDate } from '../../../utils/fileBrowserApi'

const Toast = createToast()

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const INTERNAL_DOMAIN = '@saddlefit.io'

export default function ManageAccessModal({ folder, onClose }) {
  const modalRef = useRef(null)
  const [grants, setGrants] = useState([])
  const [admins, setAdmins] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [removingEmail, setRemovingEmail] = useState(null)
  const [savingAdmin, setSavingAdmin] = useState(null)
  const [savingExternal, setSavingExternal] = useState(null)
  const [emailsInput, setEmailsInput] = useState('')
  // Access for newly added externals — write by default so their drop/upload
  // works on the files portal; pick read for view-only shares.
  const [addAccess, setAddAccess] = useState('write')

  const busy = saving || Boolean(removingEmail) || Boolean(savingAdmin) || Boolean(savingExternal)

  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target) && !busy) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose, busy])

  const loadGrants = async () => {
    try {
      const res = await getFolderGrants(folder.prefix)
      setGrants(res.grants || [])
    } catch (err) {
      console.error('Load grants failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to load access list' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadGrants()
    getFileAdmins()
      .then(res => setAdmins(res.admins || []))
      .catch(err => {
        console.error('Load admins failed:', err)
        Toast.fire({ icon: 'error', title: 'Failed to load SaddleFit team list' })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder.prefix])

  // Internal (@saddlefit.io) records carry the per-admin read/write setting;
  // everything else is an external view-only grant.
  const adminAccess = useMemo(() => {
    const map = new Map()
    for (const g of grants) if (g.internal) map.set(g.email, g.access)
    return map
  }, [grants])
  const externalGrants = useMemo(() => grants.filter(g => !g.internal), [grants])

  const handleAdminAccess = async (email, access) => {
    if (busy || adminAccess.get(email) === access) return
    setSavingAdmin(email)
    try {
      const res = await updateFolderGrants({ prefix: folder.prefix, admins: [{ email, access }] })
      if (res.errors?.length) throw new Error(res.errors[0].message)
      Toast.fire({ icon: 'success', title: `${email} set to ${access} access` })
      await loadGrants()
    } catch (err) {
      console.error('Admin access update failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to update access' })
    } finally {
      setSavingAdmin(null)
    }
  }

  // Checking grants folder access (read by default); unchecking removes it.
  const handleAdminToggle = async (email, checked) => {
    if (busy) return
    setSavingAdmin(email)
    try {
      const res = await updateFolderGrants(
        checked
          ? { prefix: folder.prefix, admins: [{ email, access: 'read' }] }
          : { prefix: folder.prefix, remove: [email] },
      )
      if (res.errors?.length) throw new Error(res.errors[0].message)
      Toast.fire({
        icon: 'success',
        title: checked ? `${email} granted read access` : `Access removed for ${email}`,
      })
      await loadGrants()
    } catch (err) {
      console.error('Admin access toggle failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to update access' })
    } finally {
      setSavingAdmin(null)
    }
  }

  const emails = useMemo(
    () =>
      emailsInput
        .split(/[\s,;]+/)
        .map(e => e.trim().toLowerCase())
        .filter(Boolean),
    [emailsInput],
  )

  const invalidEmails = emails.filter(e => !EMAIL_RE.test(e))
  const internalEmails = emails.filter(e => e.endsWith(INTERNAL_DOMAIN))
  const canAdd = emails.length > 0 && !invalidEmails.length && !internalEmails.length && !busy

  // Re-adding an existing external with a different level just overwrites the
  // grant record — no new invite email is sent for existing accounts.
  const handleExternalAccess = async (email, access) => {
    if (busy) return
    setSavingExternal(email)
    try {
      const res = await updateFolderGrants({ prefix: folder.prefix, add: [email], access })
      if (res.errors?.length) throw new Error(res.errors[0].message)
      Toast.fire({ icon: 'success', title: `${email} set to ${access} access` })
      await loadGrants()
    } catch (err) {
      console.error('External access update failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to update access' })
    } finally {
      setSavingExternal(null)
    }
  }

  const handleAdd = async () => {
    if (!canAdd) return
    setSaving(true)
    try {
      const res = await updateFolderGrants({ prefix: folder.prefix, add: emails, access: addAccess })
      const parts = []
      if (res.added?.length) parts.push(`${res.added.length} granted access`)
      if (res.invited?.length) parts.push(`${res.invited.length} invited by email`)
      if (res.errors?.length) parts.push(`${res.errors.length} failed`)
      Toast.fire({
        icon: res.errors?.length ? 'warning' : 'success',
        title: parts.join(', ') || 'No changes',
      })
      if (res.errors?.length) console.error('Grant errors:', res.errors)
      setEmailsInput('')
      await loadGrants()
    } catch (err) {
      console.error('Grant failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to grant access' })
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async email => {
    setRemovingEmail(email)
    try {
      await updateFolderGrants({ prefix: folder.prefix, remove: [email] })
      Toast.fire({ icon: 'success', title: `Access removed for ${email}` })
      await loadGrants()
    } catch (err) {
      console.error('Revoke failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to remove access' })
    } finally {
      setRemovingEmail(null)
    }
  }

  return (
    <div>
      <div className="w-full h-full fixed top-0 left-0 z-[60] bg-[#000] opacity-60"></div>

      <div
        ref={modalRef}
        className="w-[340px] sm:w-[560px] max-w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto rounded-[26px] bg-[#fff] p-8 shadow-2xl z-[70] fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 font-montserrat"
      >
        <div onClick={!busy ? onClose : undefined} className="absolute top-5 right-5">
          <img
            src={CloseIcon}
            alt="Close"
            className={`w-3 h-3 sm:w-4 sm:h-4 ${busy ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          />
        </div>

        <h4 className="text-lg font-semibold text-gray-800 m-0 mb-1">Manage access</h4>
        <p className="text-sm text-gray-500 m-0 mb-4 break-all">
          People below can access everything in{' '}
          <span className="font-medium text-[#2b364b]">{folder.prefix}</span>.
          Outside users sign in on the files portal; with Write access they can
          also upload, delete, create folders and invite others there.
        </p>

        <label className="block text-sm font-medium text-gray-700 mb-1">
          Add people
        </label>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={emailsInput}
            onChange={e => setEmailsInput(e.target.value)}
            placeholder="partner@example.com, another@example.com"
            disabled={busy}
            className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2b364b]"
          />
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden self-center">
            {['read', 'write'].map(level => (
              <button
                key={level}
                type="button"
                disabled={busy}
                onClick={() => setAddAccess(level)}
                title={level === 'read' ? 'View and download only' : 'Also upload, delete, new folders and invites'}
                className={`px-3 py-2 text-xs font-medium border-none capitalize ${
                  addAccess === level
                    ? 'bg-[#2b364b] text-white cursor-default'
                    : 'bg-white text-gray-500 hover:text-[#2b364b] cursor-pointer'
                }`}
              >
                {level}
              </button>
            ))}
          </div>
          <Button
            type="button"
            primary
            AppendIcon={FaUserPlus}
            loading={saving}
            disabled={!canAdd}
            onClick={handleAdd}
            className="create-saddle-btn !w-auto px-5 cursor-pointer"
          >
            Add
          </Button>
        </div>
        {invalidEmails.length > 0 && (
          <p className="text-red-500 text-xs mt-1 mb-0">
            Invalid: {invalidEmails.join(', ')}
          </p>
        )}
        {internalEmails.length > 0 && (
          <p className="text-amber-600 text-xs mt-1 mb-0">
            {internalEmails.join(', ')} — manage {INTERNAL_DOMAIN} access in the
            SaddleFit team section below.
          </p>
        )}
        <p className="text-xs text-gray-400 mt-1 mb-0">
          People without an account will be emailed an invitation to create one.
        </p>

        <div className="mt-5">
          <h5 className="text-sm font-semibold text-gray-700 m-0 mb-1">SaddleFit team</h5>
          <p className="text-xs text-gray-400 m-0 mb-2">
            Check an admin to give them access to this folder, then choose Read
            (view and download) or Write (also upload, delete, new folders). Once
            anyone is checked, unchecked admins can no longer see this folder —
            you are kept on the list with Write automatically so you don&apos;t lock
            yourself out. Leave everyone unchecked to keep it open to all admins.
          </p>
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <BeatLoader color="#2b364b" size={8} />
            </div>
          ) : admins.length === 0 ? (
            <p className="text-sm text-gray-500 m-0 py-2">No team accounts found.</p>
          ) : (
            <table className="w-full text-sm" style={{ borderSpacing: 0 }}>
              <tbody>
                {admins.map(admin => {
                  const access = adminAccess.get(admin.email)
                  const checked = adminAccess.has(admin.email)
                  return (
                    <tr key={admin.email} className="border-t border-gray-100">
                      <td className="py-2 pr-2 break-all">
                        <label
                          className={`flex items-center gap-2 ${busy ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={busy}
                            onChange={e => handleAdminToggle(admin.email, e.target.checked)}
                            className="w-4 h-4 shrink-0 accent-[#2b364b]"
                          />
                          <span>
                            {admin.email}
                            {admin.name && (
                              <span className="text-gray-400"> — {admin.name}</span>
                            )}
                          </span>
                        </label>
                      </td>
                      <td className="py-2" align="right">
                        {savingAdmin === admin.email ? (
                          <BeatLoader color="#2b364b" size={5} />
                        ) : (
                          checked && (
                            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                              {['read', 'write'].map(level => (
                                <button
                                  key={level}
                                  type="button"
                                  disabled={busy}
                                  onClick={() => handleAdminAccess(admin.email, level)}
                                  className={`px-3 py-1 text-xs font-medium border-none capitalize ${
                                    access === level
                                      ? 'bg-[#2b364b] text-white cursor-default'
                                      : 'bg-white text-gray-500 hover:text-[#2b364b] cursor-pointer'
                                  }`}
                                >
                                  {level}
                                </button>
                              ))}
                            </div>
                          )
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-5">
          <h5 className="text-sm font-semibold text-gray-700 m-0 mb-1">People with access</h5>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <BeatLoader color="#2b364b" size={8} />
            </div>
          ) : externalGrants.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6 m-0">
              No one outside SaddleFit has been given access to this folder yet.
            </p>
          ) : (
            <table className="w-full text-sm" style={{ borderSpacing: 0 }}>
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-2 font-medium">Email</th>
                  <th className="py-2 font-medium">Granted by</th>
                  <th className="py-2 font-medium">When</th>
                  <th className="py-2 font-medium">Access</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {externalGrants.map(grant => (
                  <tr key={grant.email} className="border-t border-gray-100">
                    <td className="py-2 pr-2 break-all">{grant.email}</td>
                    <td className="py-2 pr-2 break-all text-gray-500">
                      {grant.grantedBy || '—'}
                    </td>
                    <td className="py-2 pr-2 whitespace-nowrap text-gray-500">
                      {grant.grantedAt ? formatFileDate(grant.grantedAt) : '—'}
                    </td>
                    <td className="py-2 pr-2">
                      {savingExternal === grant.email ? (
                        <BeatLoader color="#2b364b" size={5} />
                      ) : (
                        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                          {['read', 'write'].map(level => (
                            <button
                              key={level}
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                grant.access !== level && handleExternalAccess(grant.email, level)
                              }
                              className={`px-3 py-1 text-xs font-medium border-none capitalize ${
                                grant.access === level
                                  ? 'bg-[#2b364b] text-white cursor-default'
                                  : 'bg-white text-gray-500 hover:text-[#2b364b] cursor-pointer'
                              }`}
                            >
                              {level}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-2" align="right">
                      {removingEmail === grant.email ? (
                        <BeatLoader color="#2b364b" size={5} />
                      ) : (
                        <button
                          type="button"
                          title="Remove access"
                          disabled={busy}
                          className="text-gray-500 hover:text-red-600 bg-transparent border-none cursor-pointer p-0"
                          onClick={() => handleRemove(grant.email)}
                        >
                          <FaTrashAlt size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <Button
            type="button"
            primaryOutline
            onClick={onClose}
            disabled={busy}
            className="create-saddle-btn !w-auto px-6 cursor-pointer"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}
